import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { useStyles } from "./ConfirmSubscriptionPopup.style";
import { useTranslation } from "react-i18next";

export type ConfirmSubscriptionPopupProps = {
  open: boolean;
  onConfirm?: () => void;
  onClose?: () => void;
  price?: number;
  gst?: number;
  totalAmount?: number;
  currency?: string;
  taxType?: string;
};

const ConfirmSubscriptionPopup: React.FC<ConfirmSubscriptionPopupProps> = ({
  open,
  onClose,
  onConfirm,
  price = 399,
  gst = 0,
  totalAmount,
  currency = "₹",
  taxType = "GST",
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const gstAmount = (price * gst) / 100;
  const total = totalAmount ?? price + gstAmount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      mainAction={{
        text: t("Payment_Proceed to Payment"),
        onClick: onConfirm,
        "data-gamepad": "default-selected",
      }}
      secondAction={{
        text: t("Payment_Cancel"),
        onClick: onClose,
        "data-button-sound": "back",
        id: "close-dialog",
      }}
      footer={
        <Typography
          variant={TypographyVariant.BodyB6Regular}
          color={TypographyColor.Gray}
        >
          {t("Payment_You will be redirected to the payment gateway")}
        </Typography>
      }
    >
      <div className={classes.header}>
        <Typography
          variant={TypographyVariant.HeadingH3Bold}
          color={TypographyColor.Default}
        >
          {t("Payment_Confirm Your Subscription")}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB3Medium}
          color={TypographyColor.Silver}
        >
          {t("Payment_Review your order details before proceeding")}
        </Typography>
      </div>

      <div className={classes.summaryBox}>
        <div className={classes.summaryRow}>
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            color={TypographyColor.Gray}
          >
            {t("Payment_Subscription Price")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            color={TypographyColor.Default}
          >
            {currency}
            {price}
          </Typography>
        </div>
        <div className={classes.divider} />

        <div className={classes.summaryRow}>
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            color={TypographyColor.Gray}
          >
            {taxType} ({gst}%)
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            color={TypographyColor.Default}
          >
            {currency}
            {gstAmount.toFixed(2)}
          </Typography>
        </div>
        <div className={classes.divider} />

        <div className={classes.totalRow}>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            color={TypographyColor.Default}
          >
            {t("Payment_Total Amount")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB1Bold}
            color={TypographyColor.Primary}
            className={classes.colorText}
          >
            {currency}
            {total.toFixed(2)}
          </Typography>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmSubscriptionPopup;
